/**
 * Open printable documents without about:blank/noopener failures.
 * HTML uses document.write on a real window handle (blob URLs expire / go black).
 */

/**
 * Open HTML for on-screen preview / print.
 * Must be called directly from a user click (pop-up gesture).
 */
export function openHtmlDocument(html, { title = "Desking by EnvisionDMS.com" } = {}) {
  // Empty URL keeps a same-origin window we can write into (do not use noopener).
  let w = null;
  try {
    w = window.open("", "_blank");
  } catch {
    w = null;
  }
  if (!w) {
    // iPhone/iPad Safari often blocks tabs — fall back to same-tab document write.
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      // Same-tab navigation preserves the user gesture on iOS.
      window.location.assign(url);
      return true;
    } catch {
      alert(
        "Pop-up blocked. Allow pop-ups for Desking by EnvisionDMS.com, or use Share → Print on iPhone/iPad after the page opens."
      );
      return false;
    }
  }

  try {
    w.opener = null;
  } catch {
    /* ignore */
  }

  try {
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch {
    // Fallback: blob URL if document.write is blocked — revoke only when tab closes
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      w.location = url;
      const poll = setInterval(() => {
        if (w.closed) {
          clearInterval(poll);
          URL.revokeObjectURL(url);
        }
      }, 2000);
    } catch {
      w.close();
      alert("Could not open the document preview. Please try again.");
      return false;
    }
  }

  try {
    if (title) w.document.title = title;
  } catch {
    /* ignore */
  }

  try {
    w.focus();
  } catch {
    /* ignore */
  }

  return true;
}

/** Open a PDF Blob/ArrayBuffer in a new tab for preview + print. */
export function openPdfBlob(blobOrBuffer, { title = "Desking by EnvisionDMS.com" } = {}) {
  const blob =
    blobOrBuffer instanceof Blob
      ? blobOrBuffer
      : new Blob([blobOrBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^\w.-]+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    alert(
      "Pop-up blocked — downloaded the PDF instead. Allow pop-ups to preview in a tab."
    );
    return false;
  }

  try {
    w.opener = null;
  } catch {
    /* ignore */
  }

  // Keep blob alive until the preview tab is closed (no timed revoke while open)
  const poll = setInterval(() => {
    if (w.closed) {
      clearInterval(poll);
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
  }, 2000);
  return true;
}
